/**
 * Estado de servidor das listas.
 *
 * Mais simples que o das tarefas de proposito: listas mudam raramente e nao
 * tem acao desfazivel de alta frequencia. Escrita otimista aqui adicionaria
 * complexidade sem economizar nenhuma espera perceptivel.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { uuidv7, type CreateListPayload, type UpdateListPayload } from '@cleartask/shared';
import { api } from '../../lib/api.js';
import { taskKeys } from '../tasks/useTasks.js';

export const listKeys = {
  all: ['lists'] as const,
  visible: (includeArchived: boolean) => ['lists', includeArchived] as const,
};

export function useLists(includeArchived = false) {
  return useQuery({
    queryKey: listKeys.visible(includeArchived),
    queryFn: () => api.lists.list(includeArchived),
    staleTime: 5 * 60_000,
  });
}

export function newListId(): string {
  return uuidv7();
}

export function useCreateList() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateListPayload) => api.lists.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKeys.all });
    },
  });
}

export function useUpdateList() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateListPayload }) =>
      api.lists.update(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKeys.all });
    },
  });
}

export function useDeleteList() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.lists.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKeys.all });
      // As tarefas carregam `listId`; apagar a lista muda o que a tela mostra
      // mesmo que nenhuma tarefa tenha sido tocada nesta requisicao.
      void qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}
